import { ChangeEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Checkbox, Input } from '@storybook';
import { Skeleton } from '@storybook/skeleton-loader/skeleton-loader';
import { MintCartaMessage } from 'constant';
import {
	ActiveEmailClientFormDataState,
	ActiveEmailClientMethodState,
	ActiveEmailClientState,
	EditModeState,
	EditingClientState,
	EmailClientsState,
	useEmailClient,
} from 'views/email-client/stores';
import './enter-credentials.scss';

export const EnterCredentials = () => {
	// globle states
	const activeEmailClient = useRecoilValue(ActiveEmailClientState);
	const emailClientsList = useRecoilValue(EmailClientsState);
	const [activeEmailClientMethod, setActiveEmailClientMethod] = useRecoilState(
		ActiveEmailClientMethodState
	);
	const editMode = useRecoilValue(EditModeState);
	const editingClient = useRecoilValue(EditingClientState);

	// local state
	const [authInputType, setAuthInputType] = useState('password');
	const [isLoadingClientData, setIsLoadingClientData] = useState(false);

	const { EmptyValidEmailMessage } = MintCartaMessage;
	const { methods } = activeEmailClient ?? {};
	const { credentials, key: methodKey } = useMemo(
		() =>
			methods?.find(method => method.key === activeEmailClientMethod) ?? {
				credentials: [],
				key: '',
			},
		[methods, activeEmailClientMethod]
	);

	const [credentialData, setCredentialData] = useRecoilState(
		ActiveEmailClientFormDataState(activeEmailClientMethod)
	);

	const { getSingleEmailClient } = useEmailClient();

	const initializeEditFormData = useCallback(async () => {
		setIsLoadingClientData(true);
		try {
			const resp = await getSingleEmailClient(editingClient?._id ?? '');
			if(resp?.data) {
				// Pre-populate form data with existing client config
				const formData: { [key: string]: string } = {
					isDefault: resp?.data.isActive ? 'true' : 'false',
				};
				
				// Map config data to form fields
				Object.keys(resp?.data.config).forEach(key => {
					formData[key] = String((resp?.data.config as any)[key]);
				});
				
				setCredentialData(formData);
			}
		} finally {
			setIsLoadingClientData(false);
		}
	}, [getSingleEmailClient, editingClient?._id, setCredentialData]);

	// Initialize form data for edit mode
	useEffect(() => {
		if (editMode && editingClient && activeEmailClientMethod && !credentialData?.isDefault) {
			initializeEditFormData();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editMode, editingClient, activeEmailClientMethod]);

	const handleChangeEdit = useCallback(
		(name: string, e: ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;

			setCredentialData(prev => {
				return { ...prev, [name]: value };
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[credentialData, activeEmailClientMethod]
	);

	const handleDefaultAccountCheck = useCallback(() => {
		setCredentialData(prev => ({
			...prev,
			isDefault: prev.isDefault === 'true' ? 'false' : 'true'
		}));
	}, [setCredentialData]);

	const renderMethodHeader = useMemo(
		() =>
			methods &&
			methods.length > 1 && !editMode && ( // Hide method selection in edit mode
				<div className="enter-credentials-method-header">
					{methods.map(method => (
						<div
							key={method.key}
							onClick={() => setActiveEmailClientMethod(method.key)}
							className={`method-text ${
								method.key === activeEmailClientMethod
									? 'method-text__active'
									: ''
							}`}
						>
							{method.label}
						</div>
					))}
				</div>
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeEmailClientMethod, methods, editMode]
	);

	const isEmailError = useCallback((email: string) => {
		if (email === undefined) return false;
		return !encodeURIComponent(email);
	}, []);

	const renderIcon = useMemo(() => {
		return authInputType === 'text';
	}, [authInputType]);

	const handleSuffixIcon = useCallback((id: string) => {
		const node = document.getElementById(id);
		const inputType = node?.getAttribute('type');
		if (inputType === 'text') {
			node?.setAttribute('type', 'password');
			setAuthInputType('password');
		}
		if (inputType === 'password') {
			node?.setAttribute('type', 'text');
			setAuthInputType('text');
		}
	}, []);

	const isShowDefaultCheckbox = useMemo(() => {
		const activeClient = (emailClientsList ?? []).find(client => client.isActive);
	  
		if (editMode && editingClient) {
		  return activeClient?._id?.toString() !== editingClient._id?.toString();
		}
	  
		return !!activeClient;
	  }, [emailClientsList, editMode, editingClient]);

		const renderCredentials = useMemo(
		() => {
			const filteredCredentials = credentials?.filter(credential => {
				// Hide userName field for non-SMTP clients
				if (activeEmailClient?.key !== 'smtp' && credential?.inputKey === 'userName') {
					return false;
				}
				return true;
			}) || [];

			if (isLoadingClientData) {
				return (
					<div className="enter-credentials-body">
						{/* Email field */}
						{filteredCredentials.map((_credential) => (
						<div style={{ marginBottom: '20px' }} key={_credential.inputKey}>
							<Skeleton height={12} width="60px" borderRadius={4} />
							<div style={{ marginTop: '8px' }}>
								<Skeleton height={48} width="100%" borderRadius={8} />
							</div>
						</div>
					))}
					</div>
				);
			}

			return (
				<div className="enter-credentials-body">
					{filteredCredentials.map(credential => 
						credential.type === 'checkbox' ? (
							isShowDefaultCheckbox && <Checkbox
								key={methodKey + '_' + credential.inputKey}
								label={credential.label}
								handleCheck={handleDefaultAccountCheck}
								checked={credentialData.isDefault === 'true'}
							/>
						) : (
							<Input
								autoComplete="off"
								inputType={credential.type as 'text'}
								label={credential.label}
								isRequired={credential.isRequired}
								handleChange={e => handleChangeEdit(credential.inputKey, e)}
								placeholder=""
								value={credentialData[credential.inputKey] || ''}
								id={methodKey + '_' + credential.inputKey}
								key={methodKey + '_' + credential.inputKey}
								isError={
									credential.inputKey === 'email' &&
									isEmailError(credentialData['email'] as string)
								}
								errorMessage={EmptyValidEmailMessage}
								suffixIcon={
									credential.preSuffixIcon && credential.postSuffixIcon
										? renderIcon
											? credential.postSuffixIcon
											: credential.preSuffixIcon
										: ''
								}
								handleSuffixIcon={() =>
									handleSuffixIcon(methodKey + '_' + credential.inputKey)
								}
							/>
						)
					)}
				</div>
			);
		},
		[credentials, isLoadingClientData, activeEmailClient?.key, isShowDefaultCheckbox, methodKey, handleDefaultAccountCheck, credentialData, isEmailError, EmptyValidEmailMessage, renderIcon, handleChangeEdit, handleSuffixIcon]
	);
	return (
		<div className="enter-credentials-wrapper">
			{renderMethodHeader}
			{renderCredentials}
		</div>
	);
};
